import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useTheme } from '../../theme/ThemeProvider';
import type { ThemePreference } from '../../theme/resolveTheme';
import type { LoginResponse } from '../../domain/types';

interface UserMenuProps {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  extraPills?: ReactNode;
}

const THEME_OPTIONS: { id: ThemePreference; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'System' }
];

export function UserMenu({ auth, onLogout, extraPills }: UserMenuProps): ReactElement {
  const { preference, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="user-strip">
      {extraPills}
      <div className="user-menu" ref={rootRef}>
        <button
          type="button"
          className="user-pill user-menu-trigger"
          aria-expanded={open}
          aria-controls={menuId}
          data-testid="user-menu-trigger"
          onClick={() => setOpen((v) => !v)}
        >
          {auth.displayName} ({auth.role})
        </button>
        <div
          id={menuId}
          className={`user-menu-panel${open ? ' user-menu-panel--open' : ''}`}
          aria-hidden={!open}
          role="menu"
          data-testid="user-menu-panel"
        >
          <p className="user-menu-label">Theme</p>
          {THEME_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="menuitemradio"
              aria-checked={preference === opt.id}
              className={`user-menu-item${preference === opt.id ? ' user-menu-item--active' : ''}`}
              data-testid={`user-menu-theme-${opt.id}`}
              onClick={() => setPreference(opt.id)}
            >
              {opt.label}
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            className="user-menu-item user-menu-item--logout"
            data-testid="user-menu-logout"
            onClick={() => {
              setOpen(false);
              void onLogout();
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
